import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sequence } from '@/types/sequence';

export const useSequenceFilter = () => {
  const [searchParams] = useSearchParams();
  
  // Get all filter values from URL
  const filters = {
    search: searchParams.get('search') || '',
    software: searchParams.get('software') || '',
    holiday: searchParams.get('holiday') || '',
    props: searchParams.get('props_used')?.split(',').filter(Boolean) || [],
  };

  // Query sequences with filters
  const { data: sequences = [], isLoading } = useQuery<Sequence[]>({
    queryKey: ['sequences', filters],
    queryFn: async () => {
      let query = supabase
        .from('sequences')
        .select(`
          id,
          title,
          name,
          price,
          is_free,
          software_type,
          artist_id,
          genre_id,
          user_id,
          image_url,
          album_cover_url,
          video_url,
          file_url,
          download_count,
          created_at,
          updated_at,
          featured,
          artists(name),
          genres(name),
          displays(id, name)
        `);

      // Apply text search filter
      if (filters.search) {
        query = query.or(`
          title.ilike.%${filters.search}%,
          name.ilike.%${filters.search}%
        `);
      }

      // Apply software filter
      if (filters.software) {
        query = query.eq('software_type', filters.software);
      }

      // Apply holiday filter using the correct field name
      if (filters.holiday) {
        query = query.eq('holiday_type', filters.holiday);
      }

      // Props filter: Only apply if you have a way to relate props to sequences
      // (This may need to be updated based on your schema)

      const { data, error } = await query;
      if (error) throw error;
      
      // Transform the data to match our Sequence interface
      return data?.map(item => ({
        id: item.id,
        title: item.title || item.name,
        displayName: item.displays?.name || 'Unknown Display',
        price: parseFloat(item.price || '0'),
        isFree: item.is_free || false,
        software: item.software_type as any,
        software_type: item.software_type as any,
        holiday: 'Christmas' as any, // Default until we have holiday data
        imageUrl: item.image_url || item.album_cover_url || '',
        song: {
          id: item.id,
          title: item.title || item.name,
          artist: getArtistName(item), // Use enhanced artist lookup
          album: '',
          duration: 0,
          genre: item.genres?.name,
        },
        createdAt: item.created_at,
        downloadCount: item.download_count || 0,
        downloads: item.download_count || 0,
        averageRating: 0, // Will need to implement rating system
        rating: 0,
        ratingCount: 0,
        featured: item.featured || false,
        userId: item.user_id,
        creatorId: item.user_id,
        creatorName: item.user_id ? 'Sequence Creator' : 'Unknown Creator',
        creatorAvatar: '',
      })) || [];
    }
  });

  // Enhanced artist name lookup with multiple fallback strategies
  const getArtistName = (item: any): string => {
    // First try: Direct artist join from database
    if (item.artists?.name && item.artists.name.trim() !== '') {
      return item.artists.name;
    }
    
    // Second try: Check if artist name is embedded in title
    // Common patterns: "Artist - Song Title" or "Song Title by Artist"
    if (item.title) {
      const title = item.title.toString();
      
      // Pattern: "Artist - Song Title"
      const dashMatch = title.match(/^(.+?)\s*-\s*(.+)$/);
      if (dashMatch && dashMatch[1].trim() !== '' && !dashMatch[1].toLowerCase().includes('sequence')) {
        return dashMatch[1].trim();
      }
      
      // Pattern: "Song Title by Artist"
      const byMatch = title.match(/^(.+?)\s+by\s+(.+)$/i);
      if (byMatch && byMatch[2].trim() !== '') {
        return byMatch[2].trim();
      }
    }
    
    // Third try: Check if name field has artist info
    if (item.name && item.name !== item.title) {
      const name = item.name.toString();
      const dashMatch = name.match(/^(.+?)\s*-\s*(.+)$/);
      if (dashMatch && dashMatch[1].trim() !== '' && !dashMatch[1].toLowerCase().includes('sequence')) {
        return dashMatch[1].trim();
      }
    }
    
    // Final fallback: Unknown Artist
    return 'Unknown Artist';
  };

  return {
    sequences,
    isLoading,
    filters
  };
}; 